import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { ReportSummary } from "src/app/models/models";
import { HelperService } from "src/app/util/helper.service";
import {
	Ts1PreviewCandidateDetails,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
} from "../../../ratings/container-ratings-form/ts1-rating-form-v3";
import { formatReportSummarySignatureLocal } from "../report-summary-signature-time";
import { Ts1ReportPreviewContext } from "../ts1-report-preview.utils";

@Component({
	selector: "app-ts1-v3-report-summary",
	templateUrl: "./ts1-v3-report-summary.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1V3ReportSummaryComponent {
	@Input() reportSummary?: ReportSummary;
	@Input() previewContext: Ts1ReportPreviewContext | null = null;
	@Input() candidateId = "";

	constructor(public helperService: HelperService) {}

	get instructorSignature(): string {
		return this.resolveSignatureImage(this.reportSummary?.sessionDetails?.instructorSignature);
	}

	get candidateSignature(): string {
		return this.resolveSignatureImage(this.reportSummary?.sessionDetails?.traineeSignature);
	}

	get instructorSignOffDate(): string {
		return formatReportSummarySignatureLocal(this.reportSummary?.signOffDate);
	}

	get candidateSignOffDate(): string {
		return formatReportSummarySignatureLocal(this.reportSummary?.sessionDetails?.completedOn);
	}

	/**
	 * Report/quick-summary preview contexts are built per trainee with single-entry arrays.
	 * Candidate-specific slots are resolved in buildTs1ReportPreviewContext before render.
	 */
	readonly embeddedPreviewCandidateIndex = 0;

	readonly ts1BlocksForIndex = (_index: number): Ts1ReviewCompetencyBlock[] =>
		this.previewContext?.competencyBlocks ?? [];

	readonly ts1RowsForIndex = (_index: number): Ts1ReviewManeuverRow[] => this.previewContext?.maneuverRows ?? [];

	readonly ts1CommentsForIndex = (_index: number) => this.previewContext?.summaryComments ?? [];

	readonly ts1ObCommentsForIndex = (_index: number) =>
		this.previewContext?.competencyObComments ?? this.previewContext?.summaryComments ?? [];

	readonly ts1DetailsForIndex = (_index: number): Ts1PreviewCandidateDetails | null =>
		this.previewContext?.candidateDetails ?? null;

	readonly ts1ReminderNotesEnabled = (): boolean => !!this.previewContext?.reminderNotesEnabled;

	readonly ts1StructuredReminderNotesEnabled = (): boolean =>
		!!this.previewContext?.useStructuredReminderNotes;

	readonly ts1TraineeAssessmentIdForIndex = () => this.candidateId;

	private resolveSignatureImage(signature: unknown): string {
		if (!signature) return "";

		if (typeof signature === "string") {
			if (signature.startsWith("data:")) return signature;
			return `data:image/png;base64,${signature}`;
		}

		if (ArrayBuffer.isView(signature)) {
			return this.helperService.bufferDataToImageUrl(Array.from(new Uint8Array(signature.buffer)));
		}

		const bufferData = (signature as { data?: number[] })?.data;
		return this.helperService.bufferDataToImageUrl(bufferData);
	}
}
